<template>
    <div id="checkin-kiosk-hub" class="choices container">
        <!-- 7. Session Management: every kiosk screen needs a path back home -->
        <div class="mb-2">
            <button type="button" class="btn btn-kiosk-back" @click="kioskGoHome">
                <span class="fa fa-arrow-left" :title="$t('visit-information-form.back')"></span>
            </button>
        </div>

        <!-- The heading is the account's own form title (Form Control > Pre
             Check-in / Check-in message), the same text the form behind this
             hub carries, so the guest sees one name for the form from the
             first screen on. Laid out as the check-in mockup draws it: title
             and message left-aligned, with the account's message running
             straight into the method buttons. -->
        <div class="hotel-title">
            <h2 v-html="flowTitle"></h2>
        </div>

        <!-- The account's own message for this form, straight from Form
             Control. Rich text, so it renders as saved; accounts that left it
             empty simply get the flow instruction below on its own. -->
        <div v-if="customHeader" class="hub-custom-header" v-html="customHeader"></div>

        <!-- Locked out after too many failed searches (same rule as check-in by name) -->
        <div v-if="isLockedOut" class="alert alert-danger">
            {{ $t('checkin_by_name.error_locked_out') }}
        </div>

        <!-- QR and Others are plain buttons with no panel under them, so
             anything they have to say to the guest lands here instead. -->
        <template v-if="!isLockedOut && !active_panel">
            <div v-if="info_msg" class="alert alert-info">{{ info_msg }}</div>
            <div v-if="error_msg" class="alert alert-danger">{{ error_msg }}</div>
        </template>

        <ul v-if="!isLockedOut" class="nav nav-pills mb-3 row" role="tablist">
            <!-- QR keeps its own camera flow, so it stays a plain button -->
            <li v-if="hasMethod('qr')" class="nav-item p-1 col-12" role="button">
                <a class="nav-link text-center" @click="openQrPrepare">
                    <i class="fas fa-qrcode mr-2" />
                    <div>{{ $t('virtual_kiosk.by_qr_label') }}</div>
                </a>
            </li>

            <!-- By name -->
            <template v-if="hasMethod('name')">
                <li class="nav-item p-1 col-12" role="button">
                    <a
                        class="nav-link text-center"
                        :class="{ active: active_panel === 'name' }"
                        @click="openPanel('name')"
                        v-b-toggle.kiosk-panel-name
                    >
                        <i class="fas fa-user mr-2" />
                        <div>{{ $t('virtual_kiosk.by_name_label') }}</div>
                    </a>
                </li>
                <b-collapse id="kiosk-panel-name" class="w-100" accordion="kiosk-hub-accordion" role="tabpanel">
                    <form class="kiosk-panel" @submit.prevent="submitByName">
                        <p class="panel-instruction">{{ $t('checkin_by_name.instruction') }}</p>
                        <div class="row">
                            <div class="col-md-6">
                                <div class="form-group">
                                    <label>{{ $t('checkin_by_name.label_guest_name') }}</label>
                                    <input
                                        class="form-control"
                                        type="text"
                                        v-model="guest_name"
                                        :placeholder="$t('checkin_by_name.name_placeholder')"
                                        required
                                    />
                                </div>
                            </div>
                            <div class="col-md-6">
                                <div class="form-group">
                                    <label>{{ $t('checkin_by_name.label_checkin_date') }}</label>
                                    <input
                                        class="form-control kiosk-date"
                                        :class="{ 'kiosk-date--editable': dateEditable }"
                                        :type="dateEditable ? 'date' : 'text'"
                                        :value="dateEditable ? checkinDateIso : checkin_date"
                                        :readonly="!dateEditable"
                                        :tabindex="dateEditable ? null : -1"
                                        :min="dateEditable ? todayIso : null"
                                        @click="openDatePicker"
                                        @input="onCheckinDateInput"
                                    />
                                </div>
                            </div>
                        </div>
                        <div class="text-right">
                            <button type="submit" class="btn btn-kiosk-confirm" :disabled="!guest_name.trim() || searching">
                                {{ $t('visit-information-form.confirm') }}
                            </button>
                        </div>
                        <div v-if="info_msg" class="alert alert-info mt-3 mb-0">{{ info_msg }}</div>
                        <div v-if="error_msg" class="alert alert-danger mt-3 mb-0">{{ error_msg }}</div>
                    </form>
                </b-collapse>
            </template>

            <!-- By phone -->
            <template v-if="hasMethod('phone')">
                <li class="nav-item p-1 col-12" role="button">
                    <a
                        class="nav-link text-center"
                        :class="{ active: active_panel === 'phone' }"
                        @click="openPanel('phone')"
                        v-b-toggle.kiosk-panel-phone
                    >
                        <i class="fas fa-phone-alt mr-2" />
                        <div>{{ $t('virtual_kiosk.by_phone_label') }}</div>
                    </a>
                </li>
                <b-collapse id="kiosk-panel-phone" class="w-100" accordion="kiosk-hub-accordion" role="tabpanel">
                    <!-- One phone number can hold several bookings; fall back to
                         picking the guest by (masked) name, like the PCI landing page. -->
                    <div v-if="refine_mode === 'phone'" class="kiosk-panel">
                        <div v-if="refine_step === 'list'">
                            <p class="panel-instruction">{{ $t('checkin_by_phone.instruction_list_of_guest_name') }}</p>
                            <p class="mb-2">{{ $t('checkin_by_phone.name_text') }} :</p>
                            <ul class="list-group guest-list-group">
                                <li
                                    v-for="(guest, index) in maskedGuestNames"
                                    :key="index"
                                    class="guest-list-item"
                                    :class="{ active: hovered_index === index }"
                                    @click="selectGuest(guest.original)"
                                    @mouseover="hovered_index = index"
                                    @mouseleave="hovered_index = null"
                                >
                                    <input class="form-control guest-list-input" type="text" :value="guest.masked" readonly />
                                </li>
                            </ul>
                        </div>
                        <form v-else @submit.prevent="submitRefineName">
                            <p class="panel-instruction">{{ $t('checkin_by_phone.instruction_guest_name') }}</p>
                            <div class="row">
                                <div class="col-md-6">
                                    <div class="form-group">
                                        <label>{{ $t('checkin_by_phone.label_guest_name') }} :</label>
                                        <input class="form-control" type="text" v-model="refine_name" />
                                    </div>
                                </div>
                                <div class="col-md-6">
                                    <div class="form-group">
                                        <label>{{ $t('checkin_by_phone.label_checkin_date') }}</label>
                                        <input
                                            class="form-control kiosk-date"
                                            :class="{ 'kiosk-date--editable': dateEditable }"
                                            :type="dateEditable ? 'date' : 'text'"
                                            :value="dateEditable ? checkinDateIso : checkin_date"
                                            :readonly="!dateEditable"
                                            :tabindex="dateEditable ? null : -1"
                                            :min="dateEditable ? todayIso : null"
                                            @click="openDatePicker"
                                            @input="onCheckinDateInput"
                                        />
                                    </div>
                                </div>
                            </div>
                            <div class="text-right">
                                <button type="submit" class="btn btn-kiosk-confirm" :disabled="!refine_name.trim()">
                                    {{ $t('visit-information-form.confirm') }}
                                </button>
                            </div>
                        </form>
                        <div v-if="info_msg" class="alert alert-info mt-3 mb-0">{{ info_msg }}</div>
                        <div v-if="error_msg" class="alert alert-danger mt-3 mb-0">{{ error_msg }}</div>
                    </div>
                    <form v-else class="kiosk-panel" @submit.prevent="submitByPhone">
                        <p class="panel-instruction">{{ $t('checkin_by_phone.instruction') }}</p>
                        <div class="row">
                            <div class="col-md-6">
                                <div class="form-group">
                                    <label>
                                        {{ $t('checkin_by_phone.label_phone_number') }}
                                        <i class="fas fa-info-circle text-primary" v-b-tooltip.hover
                                            :title="$t('pre_checkin_bmatch.wo_phone_tips')" />
                                    </label>
                                    <vue-tel-input
                                        mode="international"
                                        :validCharactersOnly="true"
                                        v-model="phone_raw"
                                        :placeholder="$t('pre_checkin_bmatch.wo_phone_placeholder')"
                                        @country-changed="phoneCountryChange"
                                        @input="phoneInput"
                                        required
                                    />
                                </div>
                            </div>
                            <div class="col-md-6">
                                <div class="form-group">
                                    <label>{{ $t('checkin_by_phone.label_checkin_date') }}</label>
                                    <input
                                        class="form-control kiosk-date"
                                        :class="{ 'kiosk-date--editable': dateEditable }"
                                        :type="dateEditable ? 'date' : 'text'"
                                        :value="dateEditable ? checkinDateIso : checkin_date"
                                        :readonly="!dateEditable"
                                        :tabindex="dateEditable ? null : -1"
                                        :min="dateEditable ? todayIso : null"
                                        @click="openDatePicker"
                                        @input="onCheckinDateInput"
                                    />
                                </div>
                            </div>
                        </div>
                        <div class="text-right">
                            <button type="submit" class="btn btn-kiosk-confirm" :disabled="!phone_number || searching">
                                {{ $t('visit-information-form.confirm') }}
                            </button>
                        </div>
                        <div v-if="info_msg" class="alert alert-info mt-3 mb-0">{{ info_msg }}</div>
                        <div v-if="error_msg" class="alert alert-danger mt-3 mb-0">{{ error_msg }}</div>
                    </form>
                </b-collapse>
            </template>

            <!-- By email -->
            <template v-if="hasMethod('email')">
                <li class="nav-item p-1 col-12" role="button">
                    <a
                        class="nav-link text-center"
                        :class="{ active: active_panel === 'email' }"
                        @click="openPanel('email')"
                        v-b-toggle.kiosk-panel-email
                    >
                        <i class="fas fa-envelope mr-2" />
                        <div>{{ $t('virtual_kiosk.by_email_label') }}</div>
                    </a>
                </li>
                <b-collapse id="kiosk-panel-email" class="w-100" accordion="kiosk-hub-accordion" role="tabpanel">
                    <!-- Same fallback as the phone panel: one mailbox, several bookings -->
                    <div v-if="refine_mode === 'email'" class="kiosk-panel">
                        <div v-if="refine_step === 'list'">
                            <p class="panel-instruction">{{ $t('checkin_by_phone.instruction_list_of_guest_name') }}</p>
                            <p class="mb-2">{{ $t('checkin_by_phone.name_text') }} :</p>
                            <ul class="list-group guest-list-group">
                                <li
                                    v-for="(guest, index) in maskedGuestNames"
                                    :key="index"
                                    class="guest-list-item"
                                    :class="{ active: hovered_index === index }"
                                    @click="selectGuest(guest.original)"
                                    @mouseover="hovered_index = index"
                                    @mouseleave="hovered_index = null"
                                >
                                    <input class="form-control guest-list-input" type="text" :value="guest.masked" readonly />
                                </li>
                            </ul>
                        </div>
                        <form v-else @submit.prevent="submitRefineName">
                            <p class="panel-instruction">{{ $t('checkin_by_phone.instruction_guest_name') }}</p>
                            <div class="row">
                                <div class="col-md-6">
                                    <div class="form-group">
                                        <label>{{ $t('checkin_by_phone.label_guest_name') }} :</label>
                                        <input class="form-control" type="text" v-model="refine_name" />
                                    </div>
                                </div>
                                <div class="col-md-6">
                                    <div class="form-group">
                                        <label>{{ $t('checkin_by_phone.label_checkin_date') }}</label>
                                        <input
                                            class="form-control kiosk-date"
                                            :class="{ 'kiosk-date--editable': dateEditable }"
                                            :type="dateEditable ? 'date' : 'text'"
                                            :value="dateEditable ? checkinDateIso : checkin_date"
                                            :readonly="!dateEditable"
                                            :tabindex="dateEditable ? null : -1"
                                            :min="dateEditable ? todayIso : null"
                                            @click="openDatePicker"
                                            @input="onCheckinDateInput"
                                        />
                                    </div>
                                </div>
                            </div>
                            <div class="text-right">
                                <button type="submit" class="btn btn-kiosk-confirm" :disabled="!refine_name.trim()">
                                    {{ $t('visit-information-form.confirm') }}
                                </button>
                            </div>
                        </form>
                        <div v-if="info_msg" class="alert alert-info mt-3 mb-0">{{ info_msg }}</div>
                        <div v-if="error_msg" class="alert alert-danger mt-3 mb-0">{{ error_msg }}</div>
                    </div>
                    <form v-else class="kiosk-panel" @submit.prevent="submitByEmail">
                        <p class="panel-instruction">{{ $t('virtual_kiosk.instruction_email') }}</p>
                        <div class="row">
                            <div class="col-md-6">
                                <div class="form-group">
                                    <label>{{ $t('pre_checkin_bmatch.email_label') }}</label>
                                    <input
                                        class="form-control"
                                        type="email"
                                        v-model="email"
                                        placeholder="name@example.com"
                                        required
                                    />
                                </div>
                            </div>
                            <div class="col-md-6">
                                <div class="form-group">
                                    <label>{{ $t('checkin_by_name.label_checkin_date') }}</label>
                                    <input
                                        class="form-control kiosk-date"
                                        :class="{ 'kiosk-date--editable': dateEditable }"
                                        :type="dateEditable ? 'date' : 'text'"
                                        :value="dateEditable ? checkinDateIso : checkin_date"
                                        :readonly="!dateEditable"
                                        :tabindex="dateEditable ? null : -1"
                                        :min="dateEditable ? todayIso : null"
                                        @click="openDatePicker"
                                        @input="onCheckinDateInput"
                                    />
                                </div>
                            </div>
                        </div>
                        <div class="text-right">
                            <button type="submit" class="btn btn-kiosk-confirm" :disabled="!email.trim() || searching">
                                {{ $t('visit-information-form.confirm') }}
                            </button>
                        </div>
                        <div v-if="info_msg" class="alert alert-info mt-3 mb-0">{{ info_msg }}</div>
                        <div v-if="error_msg" class="alert alert-danger mt-3 mb-0">{{ error_msg }}</div>
                    </form>
                </b-collapse>
            </template>

            <!-- By reservation number -->
            <template v-if="hasMethod('reservation_no')">
                <li class="nav-item p-1 col-12" role="button">
                    <a
                        class="nav-link text-center"
                        :class="{ active: active_panel === 'rsv' }"
                        @click="openPanel('rsv')"
                        v-b-toggle.kiosk-panel-rsv
                    >
                        <i class="fas fa-calendar-alt mr-2" />
                        <div>{{ $t('virtual_kiosk.by_reservation_label') }}</div>
                    </a>
                </li>
                <b-collapse id="kiosk-panel-rsv" class="w-100" accordion="kiosk-hub-accordion" role="tabpanel">
                    <form class="kiosk-panel" @submit.prevent="submitByReservation">
                        <p class="panel-instruction">{{ $t('virtual_kiosk.instruction_reservation') }}</p>

                        <!-- A guest holds one of two different numbers, and they
                             live in different columns: the one our own Pre
                             Check-in mail gave them, or the one the booking site
                             did. The same radios the check-in form offers, set
                             in one line on top so the row under it lines up
                             with the other panels: the number on the left, the
                             date on the right. -->
                        <div class="rsv-source">
                            <div class="custom-control custom-radio">
                                <input
                                    type="radio"
                                    class="custom-control-input"
                                    id="kiosk-rsv-pre-checkin-email"
                                    value="check-in-email"
                                    v-model="rsv_mode"
                                />
                                <label class="custom-control-label" for="kiosk-rsv-pre-checkin-email">
                                    {{ $t('visit-information-form.pre-checkin-email') }}
                                </label>
                            </div>
                            <div class="custom-control custom-radio">
                                <input
                                    type="radio"
                                    class="custom-control-input"
                                    id="kiosk-rsv-booking-site"
                                    value="booking-site"
                                    v-model="rsv_mode"
                                />
                                <label class="custom-control-label" for="kiosk-rsv-booking-site">
                                    {{ $t('visit-information-form.booking-site') }}
                                </label>
                            </div>
                        </div>

                        <div class="row">
                            <div :class="rsvWithDate ? 'col-md-6' : 'col-12'">
                                <div class="form-group">
                                    <label>{{ $t(`visit-information-form.${rsvLabel}`) }}</label>
                                    <input
                                        class="form-control"
                                        type="text"
                                        v-model="reservation_no"
                                        :placeholder="$t('visit-information-form.type-reservation-number')"
                                        required
                                    />
                                </div>
                            </div>
                            <!-- A guest checking in today must hold a booking
                                 for today, so the lookup carries today's date
                                 and the field reports it. Start Pre Check-in
                                 searches by the number alone, so it has no date
                                 to show. -->
                            <div v-if="rsvWithDate" class="col-md-6">
                                <div class="form-group">
                                    <label>{{ $t('checkin_by_name.label_checkin_date') }}</label>
                                    <input
                                        class="form-control kiosk-date"
                                        type="text"
                                        :value="checkin_date"
                                        readonly
                                        tabindex="-1"
                                    />
                                </div>
                            </div>
                        </div>
                        <div class="text-right">
                            <button type="submit" class="btn btn-kiosk-confirm" :disabled="!reservation_no.trim() || searching">
                                {{ $t('visit-information-form.confirm') }}
                            </button>
                        </div>
                        <div v-if="info_msg" class="alert alert-info mt-3 mb-0">{{ info_msg }}</div>
                        <div v-if="error_msg" class="alert alert-danger mt-3 mb-0">{{ error_msg }}</div>
                    </form>
                </b-collapse>
            </template>

            <!-- By pass — the code the pre-check-in mail carries. The lookup
                 (mode "pass-only") scopes it to the stay that covers today, so
                 the pass alone is enough to identify one booking. -->
            <template v-if="hasMethod('pass')">
                <li class="nav-item p-1 col-12" role="button">
                    <a
                        class="nav-link text-center"
                        :class="{ active: active_panel === 'pass' }"
                        @click="openPanel('pass')"
                        v-b-toggle.kiosk-panel-pass
                    >
                        <i class="fas fa-key mr-2" />
                        <div>{{ $t('virtual_kiosk.by_pass_label') }}</div>
                    </a>
                </li>
                <b-collapse id="kiosk-panel-pass" class="w-100" accordion="kiosk-hub-accordion" role="tabpanel">
                    <form class="kiosk-panel" @submit.prevent="submitByPass">
                        <p class="panel-instruction">{{ $t('virtual_kiosk.instruction_pass') }}</p>
                        <div class="row">
                            <div class="col-md-6">
                                <div class="form-group">
                                    <label>{{ $t('touch-down.pass') }}</label>
                                    <input
                                        class="form-control"
                                        type="password"
                                        v-model="pass"
                                        :placeholder="$t('touch-down.enter-pass')"
                                        required
                                    />
                                </div>
                            </div>
                            <div class="col-md-6">
                                <div class="form-group">
                                    <label>{{ $t('checkin_by_name.label_checkin_date') }}</label>
                                    <input
                                        class="form-control kiosk-date"
                                        :class="{ 'kiosk-date--editable': dateEditable }"
                                        :type="dateEditable ? 'date' : 'text'"
                                        :value="dateEditable ? checkinDateIso : checkin_date"
                                        :readonly="!dateEditable"
                                        :tabindex="dateEditable ? null : -1"
                                        :min="dateEditable ? todayIso : null"
                                        @click="openDatePicker"
                                        @input="onCheckinDateInput"
                                    />
                                </div>
                            </div>
                        </div>
                        <div class="text-right">
                            <button
                                type="submit"
                                class="btn btn-kiosk-confirm"
                                :disabled="!pass.trim() || searching"
                            >
                                {{ $t('visit-information-form.confirm') }}
                            </button>
                        </div>
                        <div v-if="info_msg" class="alert alert-info mt-3 mb-0">{{ info_msg }}</div>
                        <div v-if="error_msg" class="alert alert-danger mt-3 mb-0">{{ error_msg }}</div>
                    </form>
                </b-collapse>
            </template>

            <!-- Others — no reservation to look up. Straight to the check-in
                 form as a walk-in, exactly like the "Other" tab on the
                 touch-down check-in page. -->
            <li v-if="hasMethod('others')" class="nav-item p-1 col-12" role="button">
                <a class="nav-link text-center" @click="startWalkIn">
                    <i class="fas fa-ellipsis-h mr-2" />
                    <div>{{ $t('visit-information-form.other') }}</div>
                </a>
            </li>
        </ul>

        <!-- "Prepare QR code" step -->
        <b-modal
            id="kiosk-qr-prepare-modal"
            hide-header
            hide-footer
            centered
            @show="qr_modals_open++"
            @hidden="closeQrModal"
        >
            <div class="text-center py-3">
                <i class="fas fa-camera qr-prepare-icon" aria-hidden="true"></i>
                <p class="qr-prepare-text mt-3 mb-4">{{ $t('virtual_kiosk.prepare_qr') }}</p>
                <button type="button" class="btn btn-kiosk-primary px-5" @click="openQrScanner">
                    {{ $t('virtual_kiosk.ok') }}
                </button>
            </div>
        </b-modal>

        <!-- QR scanner (front camera), shared with the start screen -->
        <kiosk-qr-scanner-modal
            ref="qrScanner"
            @shown="qr_modals_open++"
            @hidden="closeQrModal"
            @decoded="resolveQrCode"
        />

        <kiosk-idle-warning-modal
            :visible="kiosk_idle_warning_visible"
            :resetting="kiosk_auto_resetting"
            :countdown="kiosk_idle_countdown"
            @continue-session="kioskContinueSession"
        />
    </div>
</template>

<script>
import moment from 'moment-timezone';
import _ from 'lodash';
import { booking_status } from '../../../utils/mockup';
import { KIOSK_FLOWS, kioskAutoFlow, kioskFlowMethods } from './kiosk_config';
import kioskValidationMixin from './kiosk_validation_mixin';
import KioskIdleWarningModal from './KioskIdleWarningModal.vue';
import KioskQrScannerModal from './KioskQrScannerModal.vue';
import { takeKioskScannedQr } from './kiosk_transition';
import { kioskFlowRejection } from './kiosk_booking_rules';

// Same shape check the check-out hub runs before spending an attempt.
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Failed searches allowed before the hub locks itself. The counter is shared by
// every method on purpose: on an unattended kiosk, letting a guesser reset it by
// switching from name to phone would make the limit meaningless.
const MAX_ATTEMPTS = 5;

const normalizeName = (str) => String(str ?? '').replace(/\s/g, '').toLowerCase();

// The result messages name what the guest searched with ("this phone
// number", "this QR code", ...), so the notice matches the button they
// pressed. Keyed by the message mode, which is the API mode except where one
// API mode serves two buttons: a QR code may carry a reservation number, but
// the guest only scanned a code. Name uses the default set.
const MESSAGE_GROUPS = {
    phone: 'checkin_by_phone',
    email: 'checkin_by_email',
    reservation: 'checkin_by_reservation',
    ota: 'checkin_by_reservation',
    qr: 'checkin_by_qr',
    pass: 'checkin_by_pass',
};
const DEFAULT_MESSAGE_GROUP = 'checkin_by_name';

// The line under the title, per flow: two of the flows offer the same buttons
// and only differ in what pressing them does, so the screen has to say which
// one the guest is in.

// The title above it, used only when the account has no custom form title of
// its own (see flowTitle). Confirm Pre Check-in and Start Check-in both end at
// CHECKED IN, so they are one and the same form to the guest; only Start Pre
// Check-in names itself differently.
// const FLOW_TITLES = {
//     [KIOSK_FLOWS.CONFIRM_PRE_CHECKIN]: 'virtual_kiosk.title_checkin',
//     [KIOSK_FLOWS.START_CHECKIN]: 'virtual_kiosk.title_checkin',
//     [KIOSK_FLOWS.START_PRE_CHECKIN]: 'virtual_kiosk.title_pre_checkin',
// };

export default {
    name: 'CheckinKioskHub',
    components: { KioskIdleWarningModal, KioskQrScannerModal },
    mixins: [kioskValidationMixin],
    props: {
        facility: { type: Object, default: () => ({}) },
        localized_filters: { type: Array, default: () => [] },
        // The account's custom message for this flow ({ title, header }), loaded
        // in the guest's language by the parent page. Empty until that fetch
        // lands, and for an account that never saved one.
        custom_text_setting: { type: Object, default: () => ({}) },
    },
    data() {
        return {
            // Open QR modals (prepare + scanner). Counted rather than flagged
            // because the OK button opens the scanner while the prepare modal
            // is still fading out, and a flag would blink "nobody is here"
            // exactly in the gap a closing tick could land in.
            qr_modals_open: 0,

            // Inline search panels ('name' | 'phone' | 'email' | 'rsv')
            active_panel: '',
            checkin_date: '',
            searching: false,
            info_msg: '',
            error_msg: '',
            failed_attempts: 0,
            // Which lookup the result messages are for (see MESSAGE_GROUPS)
            search_mode: '',

            guest_name: '',
            phone_raw: '',
            phone_number: '',
            phone_country_code: '',
            email: '',
            reservation_no: '',
            // Which number the guest is holding: 'check-in-email' (ours, the
            // PMS reservation number) or 'booking-site' (the OTA's). Defaults
            // to ours, same as the check-in form.
            rsv_mode: 'check-in-email',
            pass: '',

            // "Which guest are you?" fallback when one phone/email holds several bookings
            refine_mode: '',            // '' | 'phone' | 'email'
            refine_step: 'list',        // 'list' | 'name'
            refine_bookings: [],
            selected_refine_booking: null,
            refine_name: '',
            hovered_index: null,
        };
    },
    computed: {
        // Which of the three flows the guest pressed on the start screen.
        //
        // Falls back to the flow the saved mode implies, so a bookmarked hub
        // URL still works for every mode but "Confirm Pre Check-in + Start
        // Check-in" — that one has two buttons and no way to guess which was
        // meant, so the config watch below sends the guest back to press one.
        flow() {
            return this.$route.query.flow || kioskAutoFlow(this.kiosk_config?.checkin_mode);
        },
        // The methods this flow offers, minus anything the facility switched
        // off under Reservation Search.
        flowMethods() {
            return kioskFlowMethods(this.flow, this.kiosk_config?.search_methods);
        },
        // The account's message under the title, from the same custom message
        // the title comes from. Rich text saved on the admin portal, so it is
        // rendered as HTML the way the form behind this hub renders it.
        customHeader() {
            return String(this.custom_text_setting?.header ?? '').trim();
        },
        flowTitle() {
            // The parent picks the message by the flow's own `td` flag: the Pre
            // Check-in message for Start Pre Check-in, the Check-in message for
            // the two flows that end at CHECKED IN. So whichever title it holds
            // is already the one for the flow on screen.
            const custom = String(this.custom_text_setting?.title ?? '').trim();
            if (custom) return custom;

            // const key = FLOW_TITLES[this.flow];
            // return key ? this.$t(key) : '';
        },
        // Label over the reservation-number field, so it names the number the
        // guest just said they have.
        rsvLabel() {
            return this.rsv_mode === 'booking-site'
                ? 'booking-site-reservation-number'
                : 'pre-checkin-email-reservation-number';
        },
        // Guests reach this hub only through Confirm Pre Check-in / Start
        // Check-in, which both end at CHECKED IN, or through Start Pre
        // Check-in, which ends at PRE CHECK-IN. Everything the hub accepts or
        // refuses below hangs off this one distinction.
        isPreCheckinFlow() {
            return this.flow === KIOSK_FLOWS.START_PRE_CHECKIN;
        },
        isConfirmFlow() {
            return this.flow === KIOSK_FLOWS.CONFIRM_PRE_CHECKIN;
        },
        isLockedOut() {
            return this.failed_attempts >= MAX_ATTEMPTS;
        },

        // Every other flow is a guest standing at the desk today, so their
        // date is today and the field only reports it. Start Pre Check-in is
        // the guest filling the form ahead of a stay that has not begun, so
        // there the date is theirs to set and the field is a real picker.
        dateEditable() {
            return this.isPreCheckinFlow;
        },
        // A reservation number (typed or from the QR) is scoped to today's
        // check-in only where the date is fixed to today; Start Pre Check-in
        // still finds the booking by the number alone.
        rsvWithDate() {
            return !this.dateEditable;
        },
        // A native date input speaks ISO; `checkin_date` is held in the
        // YYYY/MM/DD the read-only panels print.
        checkinDateIso() {
            return this.checkin_date ? moment(this.checkin_date).format('YYYY-MM-DD') : '';
        },
        // Nothing to pre-check-in for in the past, and on a kiosk keyboard a
        // stray year is easier to type than to notice.
        todayIso() {
            return moment().format('YYYY-MM-DD');
        },

        // Someone is part-way through starting a check-in here: a panel is
        // open (which covers the "which guest are you?" step, since that keeps
        // the panel it came from), the camera is up, or a lookup is in flight.
        // MainHotelForm holds the closing transition back while this is true,
        // so the end of the window never wipes a half-typed name off the
        // screen — the guest finishes, and the kiosk closes behind them.
        guestIsEngaged() {
            if (this.isLockedOut) return false; // error screen — see the watcher below
            return this.searching || this.active_panel !== '' || this.qr_modals_open > 0;
        },
        maskedGuestNames() {
            return this.refine_bookings.map((booking) => ({
                original: booking,
                masked: this.maskName(booking.parsed.full_name),
            }));
        },
    },
    watch: {
        // MainHotelForm loads the facility after mount.
        'facility.id': {
            immediate: true,
            async handler(id) {
                if (!id || this.kiosk_config) return;
                await this.kioskLoadConfig(id);

                // Entering the hub IS the start of a session, so the operating
                // rules apply here too: a guest who deep-links this URL while
                // the Guest Hub is unconfigured or closed goes back to the home
                // screen, which shows "No Operation" / "Out of Operating Time".
                // This only runs once on entry, so a guest already searching is
                // never interrupted.
                if (!this.kioskCanStartCheckin()) return this.kioskGoHome();

                // The flow decides every rule on this screen, so a hub reached
                // without one it can resolve has nothing to show — back to the
                // start screen, where the guest presses a button that names it.
                if (!this.flow) return this.kioskGoHome();

                this.kioskStartIdleWatch();

                // The start screen's "Start Check-in by Pre-Registered QR
                // Code" button scans the code right there, before this page
                // loads, so the guest never has to find the QR method here.
                // A code it handed over is looked up as if it had just been
                // scanned on this screen: a valid one lands on the form, an
                // invalid one shows its error above the methods, which stay
                // available as the way to try again.
                const scanned = takeKioskScannedQr();
                if (scanned) this.resolveQrCode(scanned);
            },
        },

        // The end of the check-in window is watched by MainHotelForm, which
        // outlives this hub and so can hold the close back until a guest who
        // already crossed into the form is finished. All it needs from here is
        // whether anyone is part-way through the hub itself.
        guestIsEngaged: {
            immediate: true,
            handler(engaged) {
                this.$emit('busy', engaged);
            },
        },

        // 7. Session Management: the lock-out alert replaces the whole hub, so
        // it is an error screen, not a screen anyone is working on — no
        // warning to answer, just the return to the start screen a minute
        // later so the next guest finds a kiosk they can use.
        isLockedOut(locked) {
            if (!locked) return;
            this.kioskStopIdleWatch();
            this.kioskStartAutoReturn();
        },
    },
    mounted() {
        this.checkin_date = moment().format('YYYY/MM/DD');
    },
    methods: {
        // ---- Panels ----------------------------------------------------

        hasMethod(method) {
            return this.flowMethods.includes(method);
        },

        // ---- Check-in date ---------------------------------------------

        // The field is read-only in every flow but Start Pre Check-in, and
        // there it feeds the same `checkin_date` the lookups format into the
        // payload — so it is stored back in that field's own YYYY/MM/DD.
        // A half-typed date arrives here as '', which would blank the panels
        // and send an Invalid date to the lookup; the last good one stands
        // until the guest finishes typing a new one.
        onCheckinDateInput(event) {
            if (!this.dateEditable) return;

            const value = event.target.value;
            if (!value) return;

            const picked = moment(value, 'YYYY-MM-DD', true);
            if (!picked.isValid()) return;

            this.checkin_date = picked.format('YYYY/MM/DD');
        },

        // On a touch screen there is no hovering over the little calendar
        // glyph: a tap anywhere on the field should open the picker. Chrome
        // and Edge expose showPicker for exactly that; where it is missing,
        // or where the browser refuses the call, the glyph still works.
        openDatePicker(event) {
            if (!this.dateEditable) return;

            try {
                event.target.showPicker?.();
            } catch (e) {
                // Not a gesture the browser accepts — leave the glyph to it.
            }
        },

        // v-b-toggle does the open/close; this only tracks which one is active
        // so the pressed button stays highlighted and stale messages are cleared.
        // Tapping the open panel's button closes it, so drop the highlight too.
        openPanel(panel) {
            this.active_panel = this.active_panel === panel ? '' : panel;
            this.resetMessages();
            this.resetRefine();
        },

        resetMessages() {
            this.info_msg = '';
            this.error_msg = '';
        },

        resetRefine() {
            this.refine_mode = '';
            this.refine_step = 'list';
            this.refine_bookings = [];
            this.selected_refine_booking = null;
            this.refine_name = '';
            this.hovered_index = null;
        },

        errorDisplay(msg) {
            this.error_msg = msg;
            setTimeout(() => { this.error_msg = ''; }, 4000);
        },

        // Result message in the wording of the lookup that produced it.
        resultMsg(key) {
            const group = MESSAGE_GROUPS[this.search_mode] ?? DEFAULT_MESSAGE_GROUP;
            return this.$t(`${group}.${key}`);
        },

        handleFailedAttempt(msg) {
            this.failed_attempts++;
            if (this.isLockedOut) {
                // The locked-out alert replaces the whole hub.
                this.error_msg = '';
                return;
            }
            this.errorDisplay(msg);
        },

        // ---- Search ----------------------------------------------------

        // One search path for every method: hit the booking list with the
        // method's mode, then run the same reservation checks on the result.
        // `refine` marks the modes where several matches are normal (a phone
        // number or mailbox shared by a family) and the guest picks their name.
        async runSearch({ mode, search, with_date = true, refine = false, msg_mode = mode }) {
            if (this.isLockedOut || this.searching) return;

            this.resetMessages();
            this.searching = true;
            this.search_mode = msg_mode;

            const payload = { facility_id: this.facility.id, mode, search };
            if (with_date) payload.checkin_date = moment(this.checkin_date).format('YYYY-MM-DD');

            const response = await this.$lionheart
                .post('/facility-booking/list', payload)
                .then((r) => r.data)
                .catch((e) => this.catcher(e));

            this.searching = false;

            // A transport/server failure is not a wrong answer from the guest,
            // so it must not burn one of their attempts.
            if (response?.error?.has_error) {
                this.errorDisplay(response.error.msg);
                return;
            }

            const total = response?.total ?? 0;

            if (total < 1) {
                this.handleFailedAttempt(this.resultMsg('error_no_reservation'));
                return;
            }

            if (total > 1) {
                if (refine) {
                    this.startRefine(response.data);
                    return;
                }
                this.handleFailedAttempt(this.resultMsg('error_multiple_reservation'));
                return;
            }

            this.finalizeBooking(response.data[0]);
        },

        submitByName() {
            const search = this.guest_name.trim();
            if (!search) return;
            this.runSearch({ mode: 'name', search });
        },

        submitByPhone() {
            if (!this.phone_number) return;
            this.runSearch({
                mode: 'phone',
                search: `${this.phone_country_code}|${this.phone_number}`,
                refine: true,
            });
        },

        submitByEmail() {
            const search = this.email.trim();
            if (!search) return;

            // The kiosk keyboard makes typos easy; catch the obvious ones before
            // spending one of the guest's attempts on a lookup that cannot match.
            if (!EMAIL_PATTERN.test(search)) {
                this.errorDisplay(this.$t('checkout_by_phone.email_invalid'));
                return;
            }

            this.runSearch({ mode: 'email', search, refine: true });
        },

        // The two numbers are stored in different places, so they are two
        // different lookups: "reservation" matches pms_reservation_no, "ota"
        // matches the sales-channel number in the booking's metadata.
        submitByReservation() {
            const search = this.reservation_no.trim();
            if (!search) return;

            const mode = this.rsv_mode === 'booking-site' ? 'ota' : 'reservation';
            this.runSearch({ mode, search, with_date: this.rsvWithDate });
        },

        // ---- By pass ---------------------------------------------------

        submitByPass() {
            const pass = this.pass.trim();
            if (!pass) return;
            this.runPassSearch(pass);
        },

        // Mode "pass-only" matches the pass against the stay that covers today
        // (checkin_date <= today <= checkout_date), which is what makes a bare
        // pass enough on a lobby tablet: a pass from a past visit, or one
        // overheard for a stay that has not started, finds nothing.
        runPassSearch(pass) {
            return this.runLookup(
                { mode: 'pass-only', pass },
                this.$t('touch-down.error_message'),
                'pass'
            );
        },

        // The QR code from the pre-check-in mail carries "email|pass", which is
        // a different lookup: mode "pre-checkin" matches the pass against the
        // mailbox it was sent to. Falls back to the reservation's own email for
        // guests the front desk matched, the same two-step the check-out hub
        // runs.
        async runEmailPassSearch(email, pass) {
            const notFound = this.$t('touch-down.error_message');
            const found = await this.runLookup({ mode: 'pre-checkin', email, pass }, null, 'qr');

            if (found !== false) return;
            await this.runLookup({ mode: 'by-pass-email-with-formlog', email, pass }, notFound, 'qr');
        },

        // Shared tail for the /v2 lookups above. Returns false when the search
        // ran and matched nothing, so a caller can try another mode before
        // spending one of the guest's attempts on it; `notFoundMsg` of null
        // means "stay quiet, I have another mode to try". `msgMode` picks the
        // result wording (see MESSAGE_GROUPS).
        async runLookup(params, notFoundMsg, msgMode) {
            if (this.isLockedOut || this.searching) return null;

            this.resetMessages();
            this.searching = true;
            this.search_mode = msgMode;

            const response = await this.$lionheart
                .post('/v2/facility-booking/list', { facility_id: this.facility.id, ...params })
                .then((r) => r.data)
                .catch((e) => this.catcher(e));

            this.searching = false;

            // A transport/server failure is not a wrong answer from the guest,
            // so it must not burn one of their attempts.
            if (response?.error?.has_error) {
                this.errorDisplay(response.error.msg);
                return null;
            }

            const bookings = response?.data ?? [];

            if (bookings.length < 1) {
                if (notFoundMsg) this.handleFailedAttempt(notFoundMsg);
                return false;
            }

            // One pass belongs to one reservation, so more than one match is
            // not the "several bookings share a mailbox" case the phone and
            // email panels refine — take the newest, which is what every other
            // pass lookup in the app does.
            this.finalizeBooking(bookings[0]);
            return true;
        },

        // ---- Others (walk-in) ------------------------------------------

        // No reservation to find: the guest fills the check-in form and the
        // booking is created at CHECKED IN by the API, which is why only the
        // flows that end there offer this (see KIOSK_FLOW_METHODS).
        startWalkIn() {
            if (this.isLockedOut) return;
            this.resetMessages();
            this.$emit('walk-in');
        },

        // ---- "Which guest are you?" ------------------------------------

        startRefine(bookings) {
            this.refine_mode = this.active_panel;
            this.refine_step = 'list';
            this.refine_bookings = bookings;
            this.selected_refine_booking = null;
            this.refine_name = '';
            this.hovered_index = null;
        },

        selectGuest(booking) {
            this.selected_refine_booking = booking;
            this.refine_step = 'name';
            this.refine_name = '';
            this.hovered_index = null;
            this.resetMessages();
        },

        // The list only shows masked names, so the guest still has to type theirs
        // in full — and it has to be the one they tapped.
        submitRefineName() {
            const typed = normalizeName(this.refine_name);
            if (!typed) return;

            let matches = this.refine_bookings.filter(
                (b) => normalizeName(b.parsed.full_name) === typed
            );

            if (matches.length < 1) {
                this.handleFailedAttempt(this.$t('checkin_by_phone.name_does_not_match'));
                return;
            }

            // One guest, one phone, two bookings with the same name — say a
            // PRE CHECK-IN one and a RESERVED one — is a real case, and the
            // name alone cannot tell them apart. The flow can: keep only the
            // bookings the flow would go on with, and if exactly one is left
            // that is the guest's. Two that both fit (Start Check-in with a
            // PRE CHECK-IN and a RESERVED booking) stay ambiguous — picking
            // one would be a guess at which reservation to check in.
            if (matches.length > 1) {
                const fits = matches.filter((b) => this.flowRejection(b) === null);
                if (fits.length === 1) matches = fits;
            }

            if (matches.length > 1) {
                this.errorDisplay(this.$t('checkin_by_phone.error_multiple_reservation'));
                return;
            }

            if (normalizeName(matches[0].parsed.full_name)
                !== normalizeName(this.selected_refine_booking?.parsed?.full_name)) {
                this.handleFailedAttempt(this.$t('checkin_by_phone.name_does_not_match'));
                return;
            }

            this.finalizeBooking(matches[0]);
        },

        // ---- Result ----------------------------------------------------

        // Why the current flow cannot go on with this booking — `{ info }` for
        // a "nothing to do" outcome, `{ error }` for a refusal — or null when
        // it can. finalizeBooking shows the reason; submitRefineName uses it
        // to tell same-name bookings apart. The rules are the ones described
        // above finalizeBooking.
        flowRejection(booking) {
            const group = MESSAGE_GROUPS[this.search_mode] ?? DEFAULT_MESSAGE_GROUP;
            const rejection = kioskFlowRejection(booking, this.flow, group);
            if (!rejection) return null;

            return rejection.info
                ? { info: this.$t(rejection.info) }
                : { error: this.$t(rejection.error) };
        },

        // The reservation is found; whether the guest may go on with it depends
        // on the flow they pressed, because each flow ends somewhere different:
        //
        //   Confirm Pre Check-in  finishes a PRE CHECK-IN booking (the API's
        //                         /facility-booking/finish-precheckin refuses
        //                         any other status), so nothing else is
        //                         accepted here — the guest is told to start a
        //                         check-in instead.
        //   Start Check-in        runs the whole check-in. A booking that turns
        //                         out to be pre-checked-in already is not an
        //                         error: it drops into the confirmation screen,
        //                         the same way check-in by name / by phone
        //                         handles it.
        //   Start Pre Check-in    fills the Pre Check-in form, so a booking
        //                         that is already pre-checked-in has nothing
        //                         left to do here.
        //
        // Checked-in and checked-out bookings are refused by every flow: the
        // API will not re-check-in a booking (CheckinRepository@logBooking
        // rejects both statuses), so letting the guest fill a whole form first
        // would only fail at the end.
        finalizeBooking(booking) {
            const rejection = this.flowRejection(booking);
            if (rejection) {
                if (rejection.info) this.info_msg = rejection.info;
                else this.error_msg = rejection.error;
                return;
            }

            const isPreCheckedIn = booking.booking_status_id === booking_status.pre_check_in;

            const bookingDetails = _.cloneDeep({ data: [booking], total: 1 });
            this.$emit('update-booking', bookingDetails);

            // A pre-checked-in booking goes straight to the confirmation
            // screen — the guest already answered the questionnaire, so there
            // is no localized filter to pick and no form to fill.
            if (!isPreCheckedIn && this.localized_filters.length > 0) {
                this.$emit('update-next', 'localized-filters');
            } else {
                this.$emit('update-next', 'checkin-form');
            }
            this.$emit('update-action', '');
        },

        // ---- Phone input -----------------------------------------------

        phoneCountryChange(v) {
            this.phone_country_code = v.dialCode;
        },

        phoneInput(v, o) {
            // Get phone number only (excluding country code)
            this.phone_number = o.number.significant;
        },

        maskName(name) {
            const len = String(name ?? '').length;

            // Masking rule (same as check-in by phone): 2 letters: *a, 3: *aa,
            // 4: **aa, 5: **aaa, 6: ***aaa ... — only the last few stay visible.
            let visibleCount = 0;
            if (len >= 5) visibleCount = 3;
            else if (len >= 3) visibleCount = 2;
            else if (len === 2) visibleCount = 1;

            if (visibleCount === 0 || len <= 1) return name;

            const maskedLength = len - visibleCount;
            if (maskedLength <= 0) return name;

            return '*'.repeat(maskedLength) + name.slice(-visibleCount);
        },

        // ---- QR flow ---------------------------------------------------

        closeQrModal() {
            this.qr_modals_open = Math.max(0, this.qr_modals_open - 1);
        },

        openQrPrepare() {
            this.$bvModal.show('kiosk-qr-prepare-modal');
        },
        openQrScanner() {
            this.$bvModal.hide('kiosk-qr-prepare-modal');
            this.$refs.qrScanner.open();
        },

        // The pre-check-in mail encodes one of two things in the QR, decided by
        // the facility's "check-in by reservation number" setting
        // (CheckinMailer@...): "email|pass" normally, or the PMS reservation
        // number when that setting is on. Both are handled here so a guest is
        // never told their own confirmation mail is unreadable.
        resolveQrCode(value) {
            const code = String(value ?? '').trim();
            if (!code) return this.errorDisplay(this.$t('virtual_kiosk.qr_not_detected'));

            if (code.includes('|')) {
                const [email, pass] = code.split('|');
                if (!email?.trim() || !pass?.trim()) {
                    this.handleFailedAttempt(this.$t('virtual_kiosk.qr_invalid'));
                    return;
                }
                this.runEmailPassSearch(email.trim(), pass.trim());
                return;
            }

            this.runSearch({ mode: 'reservation', search: code, with_date: this.rsvWithDate, msg_mode: 'qr' });
        },
    },
};
</script>

<style lang="scss" scoped>
#checkin-kiosk-hub {
    font-family: 'Poppins', sans-serif;
    touch-action: manipulation;
    /* Fill the card (main_form.vue sizes it for the kiosk) instead of
       stopping at Bootstrap's container width inside it. */
    max-width: none;

    /* Sizes below are read off the check-in mockup at iPad width (820px) and
       scale with the viewport from there: a ~38px title over ~22px body
       text, then a stack of ~140px buttons carrying ~65px labels. */
    .hotel-title h2 {
        color: #1a5dab;
        font-family: 'Poppins';
        font-weight: 600;
        font-size: clamp(22px, 4.6vw, 40px);
        margin: 16px 0 20px;
    }

    .nav-item {
        /* 16px between buttons, over the 8px the p-1 utility gives */
        padding: 8px 4px !important;

        a.nav-link {
            min-height: clamp(96px, 17vw, 140px);
            display: flex;
            justify-content: center;
            align-items: center;
            gap: 0.4em;
            border-radius: 0;
            border: 3px solid #1489ff;
            background: #fff;
            color: #1489ff;
            font-size: clamp(28px, 8vw, 68px);
            font-weight: 700;
            line-height: 1.2;
            cursor: pointer;
            user-select: none;
            -webkit-user-select: none;

            i {
                font-size: 1em;
            }

            &.active,
            &:active {
                background: #1489ff;
                color: #fff !important;
            }
        }
    }
}

.hub-instruction {
    font-size: clamp(15px, 2.7vw, 24px);
    color: #4d4d4d;
    margin-bottom: 16px;
}

/* The account's own message. Sized with the instruction under it so the two
   read as one block, and left un-restyled otherwise: it is rich text, and the
   colors and emphasis the account saved are the point of it. */
.hub-custom-header {
    font-size: clamp(15px, 2.7vw, 24px);
    line-height: 1.5;
    color: #333;
    margin-bottom: 20px;
}

.hub-custom-header ::v-deep p:last-child {
    margin-bottom: 0;
}

/* Inline search form revealed under the pressed method button. Same look as
   the pre-check-in form's accordion (preCheckinCustomForm.vue): the field the
   guest fills on the left, the date on the right, underlined inputs on a
   plain ground. */
.kiosk-panel {
    padding: 16px 8px 8px;
    margin: 0 4px 8px;

    .panel-instruction {
        font-size: clamp(14px, 2vw, 18px);
        color: #4d4d4d;
        margin-bottom: 16px;
    }

    label {
        font-size: clamp(14px, 2vw, 18px);
        color: #4d4d4d;
        margin-bottom: 4px;
    }

    .form-control {
        min-height: 48px;
        padding-left: 0.75rem;
        padding-right: 0.75rem;
        font-size: clamp(16px, 2.2vw, 20px);
        color: #495057;
        background-color: #fff;
        border: none;
        border-bottom: 1px solid #ced4da;
        border-radius: 0;

        &:focus {
            border-color: #ced4da;
            box-shadow: none;
        }

        &::placeholder {
            color: #cccccc;
            font-size: clamp(14px, 2vw, 18px);
        }

        /* Bootstrap greys out read-only fields; the date is meant to read
           like the other input, only fixed. */
        &[readonly] {
            background-color: #fff;
        }
    }

    /* The date every panel shows is display only: today, fixed, owned by
       the lookups. A plain read-only text field like check-in by name's,
       never a native date input — Safari opens the picker on those even when
       they are read-only, and the tablet keyboard's Next/Prev can reach one
       that pointer-events alone keeps taps off. Blocked from taps and focus
       on top of that, so it is never something a guest can land on. */
    .kiosk-date:not(.kiosk-date--editable) {
        pointer-events: none;
    }

    /* Start Pre Check-in is the exception: the stay is still ahead, so the
       guest picks the date. A real native input, and the calendar glyph
       sized up to a target a finger can hit on the tablet. */
    .kiosk-date--editable {
        cursor: pointer;

        &::-webkit-calendar-picker-indicator {
            cursor: pointer;
            padding: 8px;
            margin-right: -4px;
            transform: scale(1.3);
        }
    }

    /* vue-tel-input renders its own input, so the underline and the size
       have to be pushed past the scoped boundary to reach it. */
    .vue-tel-input {
        min-height: 48px;
        border: none;
        border-bottom: 1px solid #ced4da;
        border-radius: 0;

        &:focus-within {
            border-color: #ced4da;
            box-shadow: none;
        }

        /* The flag dropdown pads itself vertically, which made the phone
           field taller than the date beside it; the 48px row is enough. */
        ::v-deep .vti__dropdown {
            padding-top: 0;
            padding-bottom: 0;
        }

        ::v-deep .vti__input,
        ::v-deep .vti__selection {
            min-height: 48px;
            font-size: clamp(16px, 2.2vw, 20px);
        }

        ::v-deep .vti__input::placeholder {
            color: #cccccc;
            font-size: clamp(14px, 2vw, 18px);
        }
    }
}

/* Reservation-number source picker, one line above the number and date. Each
   option is a row a guest can tap anywhere on; the box is grown to a
   thumb-sized target and kept in line with the text. Wraps to two lines when
   the labels do not fit side by side. */
.rsv-source {
    display: flex;
    flex-wrap: wrap;
    column-gap: 2.5rem;
    padding-top: 4px;
    margin-bottom: 1rem;

    .custom-control {
        min-height: 36px;
        padding-left: 2rem;
        display: flex;
        align-items: center;
        margin-bottom: 4px;
    }

    .custom-control-label {
        font-size: clamp(14px, 2vw, 18px);
        color: #4d4d4d;
        cursor: pointer;

        &::before,
        &::after {
            width: 1.25rem;
            height: 1.25rem;
            top: 50%;
            left: -2rem;
            transform: translateY(-50%);
        }
    }
}

.btn-kiosk-confirm {
    color: #fff;
    min-width: 156px;
    min-height: 48px;
    border-radius: 24px;
    font-weight: 600;
    border: 1px solid #1aa8de;
    background-color: #1aa8de;
    touch-action: manipulation;

    &:not([disabled]):hover,
    &:not([disabled]):active {
        color: #fff;
        border-color: #0b89bb;
        background-color: #0b89bb;
    }
}

.guest-list-group {
    .guest-list-item {
        list-style: none;
        padding: 0;
        margin-bottom: 8px;
        cursor: pointer;
    }

    .guest-list-input {
        cursor: pointer;
        text-align: center;
        transition: box-shadow 0.2s;
    }

    .guest-list-item.active .guest-list-input {
        background-color: #e3f2fd;
        color: #1976d2;
        box-shadow: 0 0 0 2px #90caf9;
    }
}

.qr-prepare-icon {
    font-size: 56px;
}
.qr-prepare-text {
    font-size: 18px;
    font-weight: 600;
}
.btn-kiosk-back {
    color: #fff;
    background-color: #1aa8de;
    min-width: 48px;
    min-height: 44px;
    touch-action: manipulation;

    &:hover,
    &:active {
        color: #fff;
        background-color: #0b89bb;
    }
}

.btn-kiosk-primary {
    color: #fff;
    background-color: #1565d8;
    min-height: 48px;
    font-weight: 600;
    touch-action: manipulation;

    &:hover,
    &:active {
        color: #fff;
        background-color: #0f4fae;
    }
}
</style>
